/**
 * User-facing order identifier helpers. All display sites (cards, headers,
 * dialogs, search hits) should funnel through these so the same order looks
 * the same everywhere, and legacy orders without an `orderNumber` degrade
 * gracefully to a short suffix of the UUID.
 *
 * Routing, cache keys, and API calls continue to use `orderId` directly —
 * `orderNumber` is display-only and is not unique across restaurants or
 * across days.
 */

interface OrderIdentity {
  orderNumber?: string | null;
  orderId: string;
}

/**
 * Full user-facing identifier. Returns the backend-supplied daily sequence
 * (e.g. "1001") when present, otherwise "#ABCDEF" built from the last 6
 * chars of the orderId for legacy orders.
 */
export function formatOrderNumber(order: OrderIdentity): string {
  if (order.orderNumber) return `#${order.orderNumber}`;
  return `#${order.orderId.slice(-6).toUpperCase()}`;
}

/**
 * Compact variant for avatar-style chips. Same as {@link formatOrderNumber}
 * for modern orders ("#1001"); for legacy orders without `orderNumber`,
 * falls back to the last 4 chars of the orderId ("#ABCD").
 */
export function formatOrderNumberCompact(order: OrderIdentity): string {
  if (order.orderNumber) return `#${order.orderNumber}`;
  return `#${order.orderId.slice(-4).toUpperCase()}`;
}
